import React, {useEffect, useState} from "react";
import {ProjectColors} from "../../../Enums/Core/projectColors";
import {Button, Col, Row, Spinner} from "react-bootstrap";
import TimeScaleFilter from "../TimeScaleFilter/TimeScaleFilter";
import StackedBarChart from "../../StackedBarChart/StackedBarChart";
import DrillDownModal from "../DrillDownModal/DrillDownModal";
import {
    DAYS, EXCEL_TITLE_NUMBER_OF_LEAKS, HIGH_FLOW, HOURS, LOW_FLOW, MODERATE_FLOW, MONTHS, WEEKS
} from "../../../Constants/constants";
import {apolloClientReports} from "../../../GraphQL/config";
import {QUERY_GET_NUMBER_OF_LEAK_ALERTS} from "../../../GraphQL/ReportGenerator/queries";
import {
    getDailyOffsetEndDate,
    getDailyStartDate,
    getDailyOffsetStartDate,
    getEndDate,
    getMonthlyEndDate,
    getMonthlyOffsetEndDate,
    getMonthlyStartDate,
    getMonthlyOffsetStartDate,
    getResolutionName,
    getWeeklyOffsetEndDate,
    getWeeklyOffsetStartDate,
    pushCsvDates,
    setCsvHeaders,
    sortResponseData,
    setDisabledOffsetButtons,
    buildCategoriesData,
    getPointResolution,
    getNextDay,
    checkStartDate,
    checkEndDate,
    createReferenceToDate,
    setDateReferenceByResolution
} from "../Utils/utils";
import NumberOfLeakAlertsChartHighResolution from "./NumberOfLeakAlertsChartHighResolution";
import {BsFillSkipStartFill, BsFillSkipEndFill} from "react-icons/bs";

export default function NumberOfLeakAlertsChart(props) {


    const [isLoading, setIsLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(props.selectedDate || null);
    const [chartData, setChartData] = useState([]);
    const [offset, setOffset] = useState(0);
    const [weekStartDate, setWeekStartDate] = useState(null);
    const [weekEndDate, setWeekEndDate] = useState(null);
    const [startDate, setStartDate] = useState(getDailyStartDate());
    const [endDate, setEndDate] = useState(getMonthlyEndDate());
    const [modalShow, setModalShow] = React.useState(false);
    const [resolution, setResolution] = useState(DAYS);
    const [categories, setCategories] = useState([]);
    const [barIndex, setBarIndex] = useState(DAYS);
    const [csvData, setCsvData] = useState([]);
    const [leftArrowDisabled, setLeftArrowDisabled] = useState(false);
    const [rightArrowDisabled, setRightArrowDisabled] = useState(true);
    const [referenceToDateDaily, setReferenceToDateDaily] = useState(null);
    const [referenceToDateWeekly, setReferenceToDateWeekly] = useState(null);

    const getNumberOfLeakAlertsFromDB = () => {
        apolloClientReports.query({
            query: QUERY_GET_NUMBER_OF_LEAK_ALERTS, variables: {
                resolution: getResolutionName(resolution),
                from: startDate.toISOString().split('T')[0],
                to: endDate.toISOString().split('T')[0]
            }
        }).then(res => {
            let response = res?.data?.leakAlertsHistogram;
            if (!response) {
                console.error(`Data from NumberOfLeakAlerts is empty`);
            } else {
                let responseData = sortResponseData(response?.data);
                setChartData([{
                    name: HIGH_FLOW,
                    data: responseData?.map((res) => res?.severity[2]?.value ? res?.severity[2]?.value : 0),
                    color: ProjectColors.DarkRed
                }, {
                    name: MODERATE_FLOW,
                    data: responseData?.map((res) => res?.severity[1]?.value ? res?.severity[1]?.value : 0),
                    color: ProjectColors.Red
                }, {
                    name: LOW_FLOW,
                    data: responseData?.map((res) => res?.severity[0]?.value ? res?.severity[0]?.value : 0),
                    color: ProjectColors.Orange
                }]);
                setDisabledOffsetButtons(responseData, offset, setRightArrowDisabled, setLeftArrowDisabled, startDate, resolution);
                setCategories(buildCategoriesData(responseData, resolution));
                setCsvData(buildCsvData(responseData));
                setIsLoading(false);
            }
        })
    }


    const buildCsvData = (responseData) => {
        let csvData = [];
        let headers = setCsvHeaders(resolution);
        headers.push('Total alerts', 'High', 'Moderate', 'Low');
        csvData.push(headers);

        responseData.forEach((entity, index) => {
            let row = [];
            let leakCounts = entity.severity.map((severityElement) => severityElement.value);
            row.push(index + 1);
            row = pushCsvDates(resolution, entity.date, row);
            row.push(leakCounts.reduce((a, b) => a + b, 0), leakCounts[2], leakCounts[1], leakCounts[0]);
            csvData.push(row);
        });

        return csvData;
    }

    useEffect(() => {
        setIsLoading(true);
        getNumberOfLeakAlertsFromDB();
    }, [startDate, endDate]);

    useEffect(() => {
        switch (resolution) {
            case MONTHS:
                setStartDate(getMonthlyOffsetStartDate(offset));
                setEndDate(getMonthlyOffsetEndDate(offset));
                break;
            case WEEKS:
                setStartDate(getWeeklyOffsetStartDate(offset));
                setEndDate(getWeeklyOffsetEndDate(offset));
                break;
            case DAYS:
                setStartDate(getDailyOffsetStartDate(offset));
                setEndDate(getDailyOffsetEndDate(offset));
                break;
        }
    }, [offset]);

    useEffect(() => {
        setIsLoading(true);
        if (offset !== 0) setOffset(0);
        let now = new Date();
        switch (resolution) {
            case HOURS:
                setStartDate(now);
                setEndDate(getEndDate(props.selectedDate, resolution));
                break;
            case DAYS:
                setStartDate(getDailyOffsetStartDate(offset));
                setEndDate(getDailyOffsetEndDate(offset));
                break;
            case WEEKS:
                setStartDate(getWeeklyOffsetStartDate(offset));
                setEndDate(getWeeklyOffsetEndDate(offset));
                break;
            default:
                setStartDate(getMonthlyStartDate());
                setEndDate(now);
                break;
        }
    }, [resolution]);

    return (<>
        {!chartData && !csvData && <Spinner animation="border" variant="primary"/>}
        {chartData && csvData && <>
            <Row>
                <Col md={6}>
                    <p className={'card-title-main bold-title'}>{props.title}</p>
                </Col>
                <Col md={3}>
                    <TimeScaleFilter
                        setOffSet={setOffset}
                        hasExportCsv
                        defaultScale={resolution}
                        setScale={setResolution}
                        csvData={csvData}
                        title={EXCEL_TITLE_NUMBER_OF_LEAKS}/>
                </Col>
            </Row>
            {isLoading && <Spinner animation="border" variant="primary"/>}
            {!isLoading && <>
                <Row className={'cardHeaderChart'}>
                    <Col md={1}><Button
                        className={'offset_button'}
                        disabled={leftArrowDisabled}
                        onClick={() => {
                            setOffset(offset - 1)
                        }}> <BsFillSkipStartFill size={30} color={'#686B73'}/>
                    </Button>
                    </Col>
                    <Col md={10}>
                        <StackedBarChart
                            categories={categories}
                            data={chartData}
                            onClick={(event) => {
                                getPointResolution(event, resolution, setSelectedDate, setBarIndex, setModalShow)
                            }}
                        />
                    </Col>
                    <Col md={1}>
                        <Button
                            className={'offset_button'}
                            disabled={rightArrowDisabled}
                            onClick={() => {
                                setOffset(offset + 1)
                            }}> <BsFillSkipEndFill size={30} color={'#686B73'}/>
                        </Button>
                    </Col>
                </Row>
                <DrillDownModal show={modalShow} onHide={() => setModalShow(false)}
                                type={NumberOfLeakAlertsChartHighResolution}
                                resolution={resolution} barIndex={barIndex} title={props.title}
                                selectedDate={selectedDate}/>
            </>}
        </>}
    </>)
}
